import { useState, useEffect, useRef, useCallback, type CSSProperties } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Level, World } from '../types/world';
import { ProofEditor } from '../components/level/ProofEditor';
import { ProofOutput } from '../components/level/ProofOutput';
import { TheoryPanel } from '../components/level/TheoryPanel';
import { HintsPanel } from '../components/level/HintsPanel';
import { TacticsAndTheoremsPanel } from '../components/level/TacticsAndTheoremsPanel';
import { SuccessModal } from '../components/level/SuccessModal';
import { SolutionViewer } from '../components/level/SolutionViewer';
import { Button } from '../components/common/Button';
import { InlineCodeText } from '../components/common/InlineCodeText';
import type { PanelSize } from '../components/common/PanelResizeHandle';
import { useGame } from '../context/GameContext';
import { useTheme } from '../context/ThemeContext';
import { useLecturerAuth } from '../context/LecturerAuthContext';
import { useProofExecution } from '../hooks/useProofExecution';
import { useJsCoq } from '../hooks/useJsCoq';
import { useJsCoqState } from '../hooks/useJsCoqState';
import toast from 'react-hot-toast';
import { makeLevelKey, matchesLevelKey } from '../utils/ids';
import { areAllTacticsAllowed, checkLearningPolicy, getLearningResources } from '../utils/learningResources';
import { completionCodeIssues } from '../utils/proofCompletion';
import { BookOpen, Eye, EyeOff, Lightbulb, Zap } from 'lucide-react';

const COQ_EDITOR_ID = 'coq-editor';
const DEFAULT_EDITOR_HEIGHT = 674;
const DEFAULT_PROOF_STATE_HEIGHT = 400;

export function LevelPage() {
  const { worldId, levelId } = useParams<{ worldId: string; levelId: string }>();
  const navigate = useNavigate();
  const { completeLevel, saveProof, loadWorldData, gameData, isLecturer } = useGame();
  const { theme } = useTheme();
  const {
    allowAllTactics: lecturerAllowsAllTactics, toggleAllTactics,
    allowAllTheorems: lecturerAllowsAllTheorems, toggleAllTheorems,
  } = useLecturerAuth();
  const { execute: executeProof, proofState, isExecuting, clearState } = useProofExecution();
  const { executeProof: jsCoqExecuteProof, isLoaded: jsCoqLoaded, isReady: jsCoqReady, initializationError, retryInitialization, setEditorValue, getEditorValue, setInitialCode, coqManager } = useJsCoq(COQ_EDITOR_ID, theme);
  const { goalState, reset: resetJsCoqState } = useJsCoqState(coqManager, jsCoqLoaded, clearState);
  
  const [level, setLevel] = useState<Level | null>(null);
  const [world, setWorld] = useState<World | null>(null);
  const language = world?.tags?.includes('tt26') && !world.id.endsWith('_en') ? 'sk' : 'en';
  const [hintsUsed, setHintsUsed] = useState(0);
  const [showHints, setShowHints] = useState(false);
  const hintsButtonRef = useRef<HTMLButtonElement>(null);
  const hintsWereOpenRef = useRef(false);
  const [attempts, setAttempts] = useState(0);
  const [startTime] = useState(Date.now());
  const [showSuccess, setShowSuccess] = useState(false);
  const [showSolution, setShowSolution] = useState(false);
  const solutionVisible = showSolution && isLecturer();
  const [theoryHeight, setTheoryHeight] = useState(400);
  const theoryWorkspaceRef = useRef<HTMLDivElement>(null);
  const [editorWidth, setEditorWidth] = useState<number | null>(null);
  const [editorHeight, setEditorHeight] = useState(DEFAULT_EDITOR_HEIGHT);
  const [proofStateHeight, setProofStateHeight] = useState(DEFAULT_PROOF_STATE_HEIGHT);
  const [proofStateWidth, setProofStateWidth] = useState<number | null>(null);
  const proofWorkspaceRef = useRef<HTMLDivElement>(null);
  const [isLevelComplete, setIsLevelComplete] = useState(false);
  const initializedLevelRef = useRef<string | null>(null);

  useEffect(() => {
    if (!jsCoqReady) {
      resetJsCoqState();
      clearState();
    }
  }, [jsCoqReady, resetJsCoqState, clearState]);

  useEffect(() => { clearState(); }, [lecturerAllowsAllTactics, lecturerAllowsAllTheorems, clearState]);

  useEffect(() => {
    setShowSuccess(false);
    setShowSolution(false);
    setHintsUsed(0);
    setShowHints(false);
  }, [worldId, levelId]);

  useEffect(() => {
    if (hintsWereOpenRef.current && !showHints) hintsButtonRef.current?.focus();
    hintsWereOpenRef.current = showHints;
  }, [showHints]);

  const closeHints = useCallback(() => setShowHints(false), []);

  const restorePanelSizes = () => {
    setTheoryHeight(400);
    setEditorWidth(null);
    setEditorHeight(DEFAULT_EDITOR_HEIGHT);
    setProofStateHeight(DEFAULT_PROOF_STATE_HEIGHT);
    setProofStateWidth(null);
    toast.success('Panel sizes restored');
  };

  const clampEditorWidth = (width: number) => {
    const available = proofWorkspaceRef.current?.clientWidth ?? width + 264;
    return Math.max(280, Math.min(width, available - 264));
  };

  const handleEditorResize = (size: PanelSize) => {
    setEditorWidth(clampEditorWidth(size.width));
    setEditorHeight(Math.max(240, size.height));
  };

  const handleTheoryResize = (size: PanelSize) => {
    if (window.matchMedia('(min-width: 1024px)').matches) {
      // The resize handle measures the full column, including the hints gutter.
      setEditorWidth(clampEditorWidth(size.width));
    }
    setTheoryHeight(Math.max(400, size.height));
  };

  const handleProofStateResize = (size: PanelSize) => {
    const available = proofWorkspaceRef.current?.clientWidth ?? size.width;
    if (solutionVisible) {
      setProofStateWidth(Math.max(240, Math.min(size.width, available)));
    } else {
      setEditorWidth(clampEditorWidth(available - 24 - Math.max(240, size.width)));
    }
    setProofStateHeight(Math.max(160, size.height));
  };

  useEffect(() => {
    async function fetchLevel() {
      if (!worldId || !levelId) return;
      
      try {
        const loadedWorld = await loadWorldData(worldId);
        if (!loadedWorld) {
          toast.error('World not found');
          navigate('/worlds');
          return;
        }
        
        setWorld(loadedWorld);
        
        const loadedLevel = loadedWorld.levels.find(l => l.id === levelId);
        if (loadedLevel) {
          setLevel(loadedLevel);
          
          // Check if level is already completed
          const isCompleted = gameData?.progress.completedLevels.some(id => matchesLevelKey(id, worldId, levelId)) || false;
          setIsLevelComplete(isCompleted);
          
          // Set initial code in jsCoq editor when it's loaded
          // Will be handled by the useEffect that checks for saved proofs
        } else {
          toast.error('Level not found');
          navigate(`/worlds/${worldId}`);
        }
      } catch (error) {
        console.error('Failed to load level:', error);
        toast.error('Failed to load level');
        navigate('/worlds');
      }
    }
    
    fetchLevel();
  }, [worldId, levelId, loadWorldData, navigate]);

  // Update editor when jsCoq loads or level changes
  // Only initialize once per level to prevent resetting user's edits
  useEffect(() => {
    if (!level || !jsCoqLoaded || !setInitialCode || !coqManager || !worldId) return;
    
    // Check if we've already initialized this level
    if (initializedLevelRef.current === level.id) {
      return;
    }
    
    // Mark this level as initialized
    initializedLevelRef.current = level.id;
    
    // Reset jsCoq state for new level
    resetJsCoqState();
    
    // Check if there's a saved proof for this completed level
    let codeToLoad = level.startingCode;
    if (gameData) {
      const levelKey = makeLevelKey(worldId, level.id);
      const savedProof = gameData.proofs[levelKey] || gameData.proofs[level.id];
      const isCompleted = gameData.progress.completedLevels.some(id => matchesLevelKey(id, worldId, level.id));
      
      // If level is completed and we have saved code, use that instead
      if (isCompleted && savedProof && savedProof.code) {
        codeToLoad = savedProof.code;
      }
    }
    
    // Set initial code - will be set when editor is available
    setInitialCode(codeToLoad);
  }, [level?.id, jsCoqLoaded, setInitialCode, setEditorValue, coqManager, resetJsCoqState, gameData]);

  useEffect(() => {
    const editors = coqManager?.provider.snippets.map(snippet => snippet.editor) ?? [];
    editors.forEach(editor => editor.on('change', clearState));
    return () => editors.forEach(editor => editor.off('change', clearState));
  }, [coqManager, jsCoqLoaded, clearState]);

  const handleRunProof = async () => {
    if (!level) return;

    setAttempts(prev => prev + 1);
    clearState();

    try {
      const code = getEditorValue ? getEditorValue() : '';
      const completionIssues = completionCodeIssues(code, level.startingCode);
      if (completionIssues.length) {
        toast.error(completionIssues.map(issue => language === 'sk' ? issue
          .replace('The editor is empty.', 'Editor je prázdny.')
          .replace('Complete all unfinished proofs.', 'Dokončite všetky nedokončené dôkazy.')
          .replace('Close all open proofs with Qed. or Defined. before checking.', 'Pred kontrolou uzavrite otvorené dôkazy pomocou Qed. alebo Defined.')
          .replace('Missing required declaration:', 'Chýba požadovaná deklarácia:') : issue).join('\n'));
        return;
      }
      if (world) {
        const resources = getLearningResources(world, level, gameData?.progress.completedLevels ?? [], gameData?.unlockedTactics ?? []);
        const issues = checkLearningPolicy(code, level, resources.lockedTheorems, {
          allowAllTactics: lecturerAllowsAllTactics,
          allowAllTheorems: lecturerAllowsAllTheorems,
        });
        if (issues.length) {
          const messages = language === 'sk' ? issues.map(issue => issue
            .replace('Tactic not yet introduced:', 'Taktika ešte nebola vysvetlená:')
            .replace('Theorem not yet available:', 'Veta ešte nie je dostupná:')
            .replace('Incomplete proof:', 'Nedokončený dôkaz:')
            .replace('Use the definitions and proof tactics taught in this level.', 'Použite definície a taktiky vysvetlené pre tento level.')
            .replace('Use the proof tactics taught in this level.', 'Použite taktiky vysvetlené pre tento level.')
            .replace('Start the tactic proof with Proof.', 'Taktický dôkaz začnite príkazom Proof.')) : issues;
          toast.error(messages.join('\n'), { duration: 7000 });
          return;
        }
      }
      const result = await executeProof(code, level, jsCoqExecuteProof, jsCoqReady);

      if (result.isComplete && !result.hasError) {
        const timeSpent = Math.floor((Date.now() - startTime) / 1000);
        const code = getEditorValue ? getEditorValue() : '';
        
        console.log('💾 Saving proof:', {
          levelId: level.id,
          codeLength: code.length,
          codePreview: code.substring(0, 100),
          timeSpent,
          hintsUsed,
          attempts
        });
        
        if (!code) {
          console.warn('⚠️ Warning: No code retrieved from editor!');
        }
        
        if (!worldId) {
          console.warn('⚠️ Missing worldId when saving proof');
          return;
        }

        saveProof(worldId, level.id, {
          code,
          completedAt: new Date().toISOString(),
          timeSpent,
          hintsUsed,
          attempts,
          correct: true,
        });
        
        console.log('✅ Proof saved, checking localStorage...');
        // Verify it was saved
        setTimeout(() => {
          const savedData = localStorage.getItem('rocq_game_data');
          if (savedData) {
            const parsed = JSON.parse(savedData);
            const savedProof = parsed.proofs?.[level.id];
            console.log('📦 Saved proof in localStorage:', {
              hasProof: !!savedProof,
              codeLength: savedProof?.code?.length || 0,
              codePreview: savedProof?.code?.substring(0, 100) || 'N/A'
            });
          } else {
            console.error('❌ No data found in localStorage!');
          }
        }, 100);

        if (worldId) {
          completeLevel(worldId, level.id);
        }
        setIsLevelComplete(true);
        setShowSuccess(true);
        toast.success('Proof complete!');
        console.log('✅ Level marked as complete');
      } else if (result.hasError) {
        toast.error('Proof has errors - check the messages below');
      } else {
        // Not complete yet - show current state
        console.log('⚠️ Proof not complete:', {
          goals: result.goals?.length || 0,
          isComplete: result.isComplete,
          hasError: result.hasError
        });
        toast.error(language === 'sk'
          ? 'Kód ešte nie je dokončený. Dokončite otvorené dôkazy a uzavrite ich pomocou Qed. alebo Defined.'
          : 'The code is not complete. Finish open proofs and close them with Qed. or Defined.');
      }
    } catch (error: any) {
      console.error('❌ Proof execution error:', error);
      toast.error(error.message || 'Failed to execute proof');
    }
  };

  const handleHintRequest = (hintNumber: number) => {
    setHintsUsed(Math.max(hintsUsed, hintNumber));
  };

  const handleNextLevel = () => {
    
    if (!world || !level) {
      console.warn('⚠️ Cannot navigate: missing world or level', { world: !!world, level: !!level });
      return;
    }
    
    const currentIndex = world.levels.findIndex((l: Level) => l.id === level.id);
    
    if (currentIndex >= 0 && currentIndex < world.levels.length - 1) {
      const nextLevel = world.levels[currentIndex + 1];
      navigate(`/worlds/${worldId}/levels/${nextLevel.id}`);
    } else {
      navigate(`/worlds/${worldId}`);
    }
  };

  if (!level) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-900 dark:text-gray-100">Loading...</div>
      </div>
    );
  }

  const resources = world ? getLearningResources(world, level, gameData?.progress.completedLevels ?? [], gameData?.unlockedTactics ?? []) : null;
  const workspaceStyle = {
    '--editor-width': editorWidth === null ? (solutionVisible ? '1fr' : '2fr') : `min(${editorWidth}px, calc(100% - 264px))`,
    '--theory-height': `${theoryHeight}px`,
  } as CSSProperties;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700">
        <div className="mx-auto px-4 py-4">
          <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-4 md:grid-cols-[auto_minmax(0,1fr)_auto]">
            <Button onClick={() => navigate(`/worlds/${worldId}`)} variant="secondary" className="whitespace-nowrap">
              ← Back to World
            </Button>
            <div className="order-last col-span-2 min-w-0 space-y-2 md:order-none md:col-span-1">
            <div className="flex flex-wrap items-center justify-center gap-3">
              <h1 className="text-2xl font-bold dark:text-gray-100"><InlineCodeText>{level.name}</InlineCodeText></h1>
              <span className="text-gray-600 dark:text-gray-400">{'★'.repeat(level.difficulty)}{'☆'.repeat(5 - level.difficulty)}</span>
            </div>
            <p className="text-center text-gray-700 dark:text-gray-300 max-w-3xl mx-auto"><InlineCodeText>{level.objective}</InlineCodeText></p>
          </div>
            <Button 
              onClick={isLevelComplete ? handleNextLevel : handleRunProof}
              disabled={isExecuting}
              className="justify-self-end whitespace-nowrap"
            >
              {isExecuting ? 'Checking…' : isLevelComplete ? 'Next Level →' : 'Check Solution'}
            </Button>
          </div>
          
        </div>
      </div>

      <div className="mx-auto px-4 py-6" style={workspaceStyle}>
        <div className="px-4">
          <div ref={proofWorkspaceRef} className={`grid grid-cols-1 lg:grid-cols-[minmax(0,var(--editor-width))_minmax(240px,1fr)] gap-6 lg:gap-y-3 items-start ${solutionVisible ? 'lg:grid-rows-[auto_auto_1fr]' : ''}`}>
            {/* The second row aligns the editor and proof state when the solution is hidden.
                On narrow screens, contents and order preserve the reading order. */}
            <div className="contents lg:block lg:col-start-1 lg:row-start-1 lg:space-y-3 min-w-0">
              <div ref={theoryWorkspaceRef} className={`relative order-1 lg:order-none min-w-0 ${showHints ? '' : 'lg:pl-24'}`}>
                {/* Hints sit below Theory on narrow screens, beside it on desktop. */}
                <div
                  className={`grid grid-cols-1 gap-6 ${showHints
                    ? 'lg:grid-cols-[minmax(220px,1fr)_minmax(0,2fr)]'
                    : ''}`}
                >
                  <div hidden={!showHints} className="relative min-w-0 h-[var(--theory-height,400px)]">
                    <HintsPanel
                      key={`${worldId}:${level.id}`}
                      hints={level.hints}
                      isOpen={showHints}
                      onClose={closeHints}
                      onHintRequest={handleHintRequest}
                    />
                  </div>
                  {/* Theory Window */}
                  <div className="relative order-first lg:order-none min-w-0 h-[var(--theory-height,400px)]">
                    <TheoryPanel
                      theory={level.theory}
                      resizeWidthRef={theoryWorkspaceRef}
                      onResize={handleTheoryResize}
                      onRestoreLayout={restorePanelSizes}
                    />
                  </div>
                </div>
                <div hidden={showHints} className="mt-3 lg:mt-0 lg:absolute lg:left-0 lg:top-3">
                  <button
                    ref={hintsButtonRef}
                    type="button"
                    onClick={() => setShowHints(true)}
                    aria-label="Open hints"
                    aria-expanded={showHints}
                    aria-controls="level-hints"
                    title="Hints"
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    <Lightbulb className="text-warning-500 dark:text-warning-400" size={20} aria-hidden="true" />
                    <span>Hints</span>
                  </button>
                </div>
              </div>

              {isLecturer() && (
                <div className="order-3 lg:order-none flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="secondary" size="sm" role="switch" aria-checked={lecturerAllowsAllTactics} aria-label={language === 'sk' ? 'Povoliť všetky taktiky' : 'Allow All Tactics'} title={language === 'sk' ? 'Platí iba pre vaše kontroly počas tohto prihlásenia.' : 'Applies only to your checks during this login.'} disabled={isExecuting} onClick={toggleAllTactics} className="flex items-center gap-2">
                    <Zap size={16} aria-hidden="true" />
                    {lecturerAllowsAllTactics && <span aria-hidden="true">✓</span>}
                    {language === 'sk' ? 'Všetky taktiky pre mňa' : 'Allow All Tactics'}
                  </Button>
                  <Button type="button" variant="secondary" size="sm" role="switch" aria-checked={lecturerAllowsAllTheorems} aria-label={language === 'sk' ? 'Povoliť všetky vety' : 'Allow All Theorems'} title={language === 'sk' ? 'Vypne zákazy viet a podmienky ich sprístupnenia iba pre vaše kontroly počas tohto prihlásenia.' : 'Disables forbidden and locked theorem checks only for your checks during this login.'} disabled={isExecuting} onClick={toggleAllTheorems} className="flex items-center gap-2">
                    <BookOpen size={16} aria-hidden="true" />
                    {lecturerAllowsAllTheorems && <span aria-hidden="true">✓</span>}
                    {language === 'sk' ? 'Všetky vety pre mňa' : 'Allow All Theorems'}
                  </Button>
                  <Button variant="secondary" size="sm" aria-expanded={solutionVisible} aria-controls="level-solution" onClick={() => setShowSolution(previous => !previous)} className="flex items-center gap-2">
                    {solutionVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                    {solutionVisible ? 'Hide Solution' : 'Show Solution'}
                  </Button>
                </div>
              )}
            </div>

            {/* Keep the editor mounted while showing or hiding the solution. */}
            <div className={`order-4 lg:order-none lg:col-start-1 lg:row-start-2 min-w-0 ${solutionVisible ? 'lg:row-span-2' : ''}`} style={{ height: editorHeight }}>
              <ProofEditor
                containerId={COQ_EDITOR_ID}
                isLoaded={jsCoqLoaded}
                isReady={jsCoqReady}
                initializationError={initializationError}
                onRetry={retryInitialization}
                onResize={handleEditorResize}
              />
            </div>

            {/* Reference lists scroll within the first row. The solution has its
                own row below them so it can push Proof State down. */}
            <div className="contents lg:block lg:relative lg:col-start-2 lg:row-start-1 lg:self-stretch min-w-0">
              <div className="contents lg:block lg:absolute lg:inset-0 lg:space-y-3 lg:overflow-y-auto">
                <div className="order-2 lg:order-none min-w-0">
                  <TacticsAndTheoremsPanel
                    unlockedTactics={resources?.tactics ?? []}
                    availableTheorems={resources?.theorems ?? []}
                    courseRestricted={level.allowedTactics !== undefined || level.allowAllTactics === true}
                    allTacticsAllowed={areAllTacticsAllowed(level, lecturerAllowsAllTactics)}
                    lecturerOverride={lecturerAllowsAllTactics}
                    lecturerTheoremOverride={lecturerAllowsAllTheorems}
                    language={language}
                  />
                </div>
              </div>
            </div>

            {solutionVisible && worldId && (
              <div className="order-5 lg:order-none lg:col-start-2 lg:row-start-2 min-w-0">
                <SolutionViewer key={`${worldId}:${level.id}`} level={level} worldId={worldId} onClose={() => setShowSolution(false)} />
              </div>
            )}

            {/* Right: Proof State & Messages */}
            <div className={`order-6 lg:order-none lg:col-start-2 min-w-0 ${solutionVisible ? 'lg:row-start-3' : 'lg:row-start-2'}`} style={{ height: proofStateHeight, width: solutionVisible && proofStateWidth !== null ? `min(100%, ${proofStateWidth}px)` : undefined, marginLeft: solutionVisible ? 'auto' : undefined }}>
              <ProofOutput
                state={jsCoqReady ? proofState || goalState : null}
                initializationMessage={jsCoqReady ? undefined : initializationError || 'Waiting for Rocq to become ready…'}
                isExecuting={isExecuting}
                onResize={handleProofStateResize}
              />
            </div>
          </div>
        </div>
      </div>

      {showSuccess && (
        <SuccessModal
          level={level}
          hintsUsed={hintsUsed}
          timeSpent={Math.floor((Date.now() - startTime) / 1000)}
          onClose={() => setShowSuccess(false)}
          onNext={handleNextLevel}
        />
      )}
    </div>
  );
}
